import React from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Switch,
  TextField,
  Alert,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const steps = [
  "Plan Details",
  "POP Validity",
  "Scope Type",
  "Scope Selection",
  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const ConstraintCard = ({
  title,
  subtitle,
  enabled,
  onToggle,
  min,
  max,
  onMinChange,
  onMaxChange,
  error,
}) => (
  <Box
    sx={{
      mb: 2,
      border: "1px solid",
      borderColor: enabled ? "primary.main" : "divider",
      borderRadius: 2,
      p: 2.5,
      backgroundColor: enabled ? "#F8FAFF" : "#fff",
    }}
  >
    <Box display="flex" justifyContent="space-between" mb={1}>
      <Box>
        <Typography fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      <Switch checked={enabled} onChange={onToggle} />
    </Box>

    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
      <TextField
        size="small"
        label="Minimum"
        type="number"
        placeholder="e.g. 10"
        disabled={!enabled}
        value={min}
        onChange={onMinChange}
        inputProps={{ min: 0 }}
      />

      <TextField
        size="small"
        label="Maximum (optional)"
        type="number"
        placeholder="Leave empty to ignore"
        disabled={!enabled}
        value={max}
        onChange={onMaxChange}
        inputProps={{ min: 0 }}
      />
    </Box>

    {enabled && error && (
      <Typography
        variant="caption"
        color="warning.main"
        display="block"
        mt={1}
      >
        Please enter at least one value or disable this constraint.
      </Typography>
    )}
  </Box>
);

const CreatePopPlanStep6 = ({ data, setData, onBack, onNext }) => {
  const eligibility = data.eligibility;

  const update = (field, value) => {
    setData({
      ...data,
      eligibility: { ...eligibility, [field]: value },
    });
  };

  const isSiteVisitInvalid =
    eligibility.siteVisitEnabled &&
    !eligibility.siteVisitMin &&
    !eligibility.siteVisitMax;

  const isFosInvalid =
    eligibility.fosEnabled &&
    !eligibility.fosMin &&
    !eligibility.fosMax;

  const isNegative =
    eligibility.siteVisitMin < 0 ||
    eligibility.siteVisitMax < 0 ||
    eligibility.fosMin < 0 ||
    eligibility.fosMax < 0;

  return (
    <Box p={1}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{ mb: 2, cursor: "pointer", color: "text.secondary" }}
        onClick={onBack}
      >
        ← Back to POPs
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={5} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  width: 36,
                  height: 36,
                  fontSize: "1rem",
                  "&.Mui-completed": { color: "#2563EB" },
                  "&.Mui-active": {
                    color: "#2563EB",
                    transform: "scale(1.15)",
                  },
                },
              }}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: "0.85rem",
                  mt: 1,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography variant="subtitle2" color="text.secondary" mb={2}>
        Step 6 of 9: <strong>Eligibility</strong> — Optional constraints
      </Typography>

      {/* <Alert severity="info" sx={{ mb: 2 }}>
        Apply additional conditions that CPs must meet to qualify for this
        plan. All enabled conditions must be satisfied (AND logic).
      </Alert> */}

      <Alert severity="warning" sx={{ mb: 3 }}>
        Constraints will be evaluated only for this POP scope.
      </Alert>

      {/* Constraints */}
      <ConstraintCard
        title="Site Visit Constraint"
        subtitle="Ensure CPs are actively generating site visits"
        enabled={eligibility.siteVisitEnabled}
        onToggle={(e) => {
          const checked = e.target.checked;
          setData({
            ...data,
            eligibility: {
              ...eligibility,
              siteVisitEnabled: checked,
              siteVisitMin: checked ? eligibility.siteVisitMin : "",
              siteVisitMax: checked ? eligibility.siteVisitMax : "",
            },
          });
        }}
        min={eligibility.siteVisitMin}
        max={eligibility.siteVisitMax}
        onMinChange={(e) => update("siteVisitMin", e.target.value)}
        onMaxChange={(e) => update("siteVisitMax", e.target.value)}
        error={isSiteVisitInvalid}
      />

      <ConstraintCard
        title="FOS (CP Executive) Count Constraint"
        subtitle="Reward CPs who invest in on-ground sales effort"
        enabled={eligibility.fosEnabled}
        onToggle={(e) => {
          const checked = e.target.checked;
          setData({
            ...data,
            eligibility: {
              ...eligibility,
              fosEnabled: checked,
              fosMin: checked ? eligibility.fosMin : "",
              fosMax: checked ? eligibility.fosMax : "",
            },
          });
        }}
        min={eligibility.fosMin}
        max={eligibility.fosMax}
        onMinChange={(e) => update("fosMin", e.target.value)}
        onMaxChange={(e) => update("fosMax", e.target.value)}
        error={isFosInvalid}
      />

      <Alert severity="warning" sx={{ mb: 3 }}>
        Additional brokerage is applied only when all enabled
        constraints are satisfied.
      </Alert>

      {/* Preview (Flat Box) */}
      <Box
        sx={{
          backgroundColor: "#EFF6FF",
          border: "1px solid #DBEAFE",
          borderRadius: 2,
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography fontWeight={600} mb={1}>
          Eligibility Check Preview (Sample CP)
        </Typography>

        <Divider sx={{ mb: 1 }} />

        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2">Site Visits</Typography>
          <Typography variant="body2" color="primary">
            {eligibility.siteVisitMin || "N/A"} / Min Required:{" "}
            {eligibility.siteVisitMin || "N/A"}
          </Typography>
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2">FOS Count</Typography>
          <Typography variant="body2" color="primary">
            {eligibility.fosMin || "N/A"} / Min Required:{" "}
            {eligibility.fosMin || "N/A"}
          </Typography>
        </Box>

        <Chip
          label="POP Applicable"
          color="success"
          size="small"
          sx={{ mt: 1 }}
        />
      </Box>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between">
        <Button variant="outlined" onClick={onBack}>
          ← Back
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          disabled={isSiteVisitInvalid || isFosInvalid || isNegative}
        >
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep6;